import { type MissionTotals } from '@avity/engine'

/**
 * Real-time totals strip.
 *
 * Sits just above the submit button on the mission form so the
 * driver always sees the consequence of the slots / bonuses they
 * just typed:
 *
 *     ┌───────────────────────────────────────────┐
 *     │ Total 8h15 · Jour 6h45 · Nuit 1h30 · +2h00 │
 *     └───────────────────────────────────────────┘
 *
 * Computed by the parent (it owns the slots + washing + travel
 * state). We keep this component dumb — purely presentational —
 * because the same totals object is also serialized later for
 * debugging / logging, and we don't want two sources of truth.
 *
 * The minutes-to-display conversion uses the engine's
 * `MissionTotals.totalMinutes / dayMinutes / nightMinutes /
 * bonusMinutes` directly so we round the same way as billing
 * (15-min ceiling) without re-implementing it here.
 */

interface TotalsDisplayProps {
  totals: MissionTotals
  /**
   * When true, render the strip in a "warning" tone — used by the
   * form to signal the driver hasn't entered any usable slot yet.
   */
  empty?: boolean
}

export function TotalsDisplay({ totals, empty = false }: TotalsDisplayProps) {
  return (
    <div
      className={[
        'rounded-xl border-2 px-4 py-3',
        'flex flex-wrap items-center justify-between gap-x-4 gap-y-1',
        'text-sm font-medium',
        empty
          ? 'bg-warning-soft border-warning text-warning'
          : 'bg-info-soft border-info text-info',
      ].join(' ')}
      role="status"
      aria-live="polite"
    >
      <Stat label="Total" value={formatMinutes(totals.totalMinutes)} primary />
      <Stat label="Jour" value={formatMinutes(totals.dayMinutes)} />
      <Stat label="Nuit" value={formatMinutes(totals.nightMinutes)} />
      {totals.bonusMinutes > 0 ? (
        <Stat label="Bonus" value={`+${formatMinutes(totals.bonusMinutes)}`} />
      ) : null}
    </div>
  )
}

interface StatProps {
  label: string
  value: string
  primary?: boolean
}

function Stat({ label, value, primary = false }: StatProps) {
  return (
    <span className="inline-flex items-baseline gap-1.5">
      <span className="text-xs uppercase tracking-wide opacity-70">{label}</span>
      <span
        className={[
          'font-mono tabular-nums',
          primary ? 'text-base font-bold' : '',
        ].join(' ')}
      >
        {value}
      </span>
    </span>
  )
}

function formatMinutes(total: number): string {
  if (total <= 0) return '0h00'
  const hours = Math.floor(total / 60)
  const minutes = total % 60
  return `${hours}h${String(minutes).padStart(2, '0')}`
}
